/**
 * Formatting helpers for `notification_preferences.display_currency` (in-app display only).
 * Does not convert amounts between currencies.
 */

const ZERO_MINOR_UNIT = new Set(["JPY", "VND", "IDR", "KRW"]);

function fractionDigitsForCurrency(currency: string): { min: number; max: number } {
  const c = currency.toUpperCase();
  if (c === "XAU") return { min: 3, max: 3 };
  if (ZERO_MINOR_UNIT.has(c)) return { min: 0, max: 0 };
  return { min: 2, max: 2 };
}

export function formatDisplayCurrency(
  value: number,
  currency: string,
  options: { showSign?: boolean; locale?: string } = {}
): string {
  const { showSign = false, locale = "en-US" } = options;
  const sign = showSign && value >= 0 ? "+" : "";
  const code = (currency || "USD").toUpperCase();
  const { min, max } = fractionDigitsForCurrency(code);
  try {
    return `${sign}${new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(value)}`;
  } catch {
    return `${sign}${new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)}`;
  }
}

/** Shorter / compact labels (e.g. calendar tiles) using the same display currency. */
export function formatDisplayCurrencyCompact(
  value: number,
  currency: string,
  locale: string = "en-US"
): string {
  const code = (currency || "USD").toUpperCase();
  const { min, max } = fractionDigitsForCurrency(code);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      notation: Math.abs(value) >= 1000 ? "compact" : "standard",
    }).format(value);
  } catch {
    return formatDisplayCurrency(value, "USD", { locale });
  }
}
